import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SwapIcon } from '../../../../assets/icons/arrange-circle-icon.svg';
// import { ReactComponent as ExportIcon } from '../../../assets/icons/export.svg';
// import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { addSelectedStationIds } from '../../../../redux/slices/StationComparisonSlice';
import type { RootState } from '../../../../redux/store';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
// import MyButton from '../../../shared-components/button/Button';
// import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import ExportButton from '../../../../shared-components/export-button/ExportButton';
import SelectField from '../../../../shared-components/select-field/SelectField';
import ToggleWithIcons from '../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationDetailsApi from '../../../../utils/api/station-management';

import StationCard from './StationCard';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface StationMetric {
  label: string;
  key: string;
}

export interface StationData {
  _id: string;
  name: string;
  location: string;
  totalRevenue: number;
  users: number;
  deviceCount: number;
  energyDispensed: string;
  connectors: number;
  uptime: string;
  chargingSessions: number;
  avgSessionDuration: string;
  healthScore: number;
  faultCount: number;
}

interface SelectOption {
  label: string;
  value: string;
}

/* -------------------------------------------------------------------------- */
/*                              Static Constants                              */
/* -------------------------------------------------------------------------- */

const MAX_COMPARE_COUNT = 4;

/* -------------------------------------------------------------------------- */
/*                              API Helper                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetch station details.
 * @param {string} id - Station ID.
 * @returns {Promise<StationData | null>} Station details or null.
 */
const fetchStation = async (id: string): Promise<StationData | null> => {
  try {
    const response = await stationDetailsApi.fetchStationDetails(id);
    return response.data as StationData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch station ${id}`, error);
    return null;
  }
};

/**
 * StationComparisonList
 * @returns {any} The rendered component
 */
function StationComparisonList(): JSX.Element {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const { chargingStationList, selectedChargingPoint } = useAppSelector(
    (state: RootState) => state.stationManagement,
  );

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const [selectedDevices, setSelectedDevices] = useState<string[]>(() => {
    const initialDevices = Array(MAX_COMPARE_COUNT).fill('');
    if (selectedChargingPoint?.id) {
      initialDevices[0] = selectedChargingPoint.id;
    }
    return initialDevices;
  });

  const [detailCache, setDetailCache] = useState<Record<string, StationData>>({});

  /**
   * Tracks every station ID that has been fetched (success or failure).
   * Using a ref means it never triggers re-renders and persists across cycles,
   * so a failed API call is never retried automatically.
   */
  const fetchedIds = useRef<Set<string>>(new Set());

  const [chartToggle, setChartToggle] = useState([
    { label: t('fullDetails'), key: 'full', isActive: true },
    { label: t('differenceOnly'), key: 'diff', isActive: false },
  ]);

  const METRICS: StationMetric[] = useMemo(
    () => [
      { label: t('stationLocation'), key: 'address' },
      { label: t('totalRevenueThisMonth'), key: 'totalRevenue' },
      { label: t('users'), key: 'consumerCount' },
      { label: t('deviceCount'), key: 'deviceCount' },
      { label: t('energyDispensedKwh'), key: 'electricityCostByUtilityProvider' },
      { label: t('connectors'), key: 'totalConnectors' },
      { label: t('uptimeLast30Days'), key: 'uptimeLast30Days' },
      { label: t('noOfChargingSessions'), key: 'totalChargingSession' },
      { label: t('avgSessionDuration'), key: 'avgChargingHours' },
      { label: t('healthScore'), key: 'healthScore' },
      { label: t('faultCountLast30Days'), key: 'faultsCount' },
    ],
    [t],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Derived State                               */
  /* ------------------------------------------------------------------------ */

  const detailedStations = useMemo(
    () =>
      selectedDevices
        .map((id) => detailCache[id])
        .filter((station): station is StationData => Boolean(station)),
    [detailCache, selectedDevices],
  );

  const orderedDeviceOptions: SelectOption[] = useMemo(() => {
    if (!chargingStationList.length) {
      return [];
    }

    const selected = selectedDevices
      .filter(Boolean)
      .map((id) => chargingStationList.find((s) => s._id === id))
      .filter((station): station is { _id: string; name: string } => Boolean(station));

    const remaining = chargingStationList.filter(
      (station) => !selectedDevices.includes(station._id),
    );

    return [...selected, ...remaining].map((station) => ({
      label: station.name ? t(station.name) : station.name,
      value: station._id,
    }));
  }, [chargingStationList, selectedDevices]);

  /**
   * Number of visible selectors based on selection progress
   */
  const visibleSelectorCount = useMemo(() => {
    const firstEmptyIndex = selectedDevices.findIndex((id) => !id);

    // If none are empty, show all
    if (firstEmptyIndex === -1) {
      return MAX_COMPARE_COUNT;
    }

    // Always show at least one selector
    return Math.max(1, firstEmptyIndex + 1);
  }, [selectedDevices]);

  /**
   * Handle export
   */
  const handleExport = async (type: string) => {
    const stationIds = selectedDevices.filter(Boolean);

    if (!stationIds.length) return;
    setIsFetching(true);

    try {
      const stations = (await Promise.all(stationIds.map((id) => fetchStation(id)))).filter(
        Boolean,
      ) as StationData[];

      const headers = [t('stationComparison'), ...stations.map((station) => station.name)];

      const rows = METRICS.map((metric) => [
        metric.label,
        ...stations.map((station) => {
          let value: any = '-';
          switch (metric.key) {
            case 'address':
              value = station.location || (station as any).address;
              break;
            case 'totalRevenue':
              value = station.totalRevenue;
              break;
            case 'consumerCount':
              value = station.users || (station as any).consumerCount;
              break;
            case 'deviceCount':
              value = station.deviceCount;
              break;
            case 'electricityCostByUtilityProvider':
              value = station.energyDispensed || (station as any).electricityCostByUtilityProvider;
              break;
            case 'totalConnectors':
              value = station.connectors || (station as any).totalConnectors;
              break;
            case 'uptimeLast30Days':
              value = station.uptime || (station as any).uptimeLast30Days;
              break;
            case 'totalChargingSession':
              value = station.chargingSessions || (station as any).totalChargingSession;
              break;
            case 'avgChargingHours':
              value = station.avgSessionDuration || (station as any).avgChargingHours;
              break;
            case 'healthScore':
              value = station.healthScore;
              break;
            case 'faultsCount':
              value = station.faultCount || (station as any).faultsCount;
              break;
            default:
              value = (station as any)[metric.key];
          }

          if (value === undefined || value === null || value === '') {
            return '-';
          }

          if (typeof value === 'number') {
            if (!Number.isInteger(value)) {
              return String(ToFixedValue(value));
            }
            return String(value);
          }

          return String(value);
        }),
      ]);

      if (type === 'csv') {
        exportCSV(headers, rows, 'Station Comparison.csv');
      } else {
        exportPDF(headers, rows, 'Station Comparison.pdf');
      }
    } finally {
      setIsFetching(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Effects                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (selectedChargingPoint?.id && !selectedDevices[0]) {
      setSelectedDevices((previous) => {
        const updated = [...previous];
        updated[0] = selectedChargingPoint.id;
        return updated;
      });
    }
  }, [selectedChargingPoint, selectedDevices]);

  useEffect(() => {
    // Only fetch IDs that have never been attempted before (success OR failure).
    // The fetchedIds ref is checked — not detailCache — so failed calls are never retried.
    const idsToFetch = selectedDevices.filter((id) => Boolean(id) && !fetchedIds.current.has(id));

    if (!idsToFetch.length) {
      return;
    }

    // Mark all IDs as attempted immediately to prevent concurrent duplicate calls.
    idsToFetch.forEach((id) => fetchedIds.current.add(id));

    setIsFetching(true);

    idsToFetch.forEach((id, index) => {
      dispatch(addSelectedStationIds({ index, id }));
    });

    /**
     * Fetch all missing stations and merge successful results into the cache.
     * Failed stations are already recorded in fetchedIds so they won't be retried.
     */
    const fetchAllStations = async () => {
      const results = await Promise.all(idsToFetch.map((id) => fetchStation(id)));

      setDetailCache((previous) => {
        const updated = { ...previous };
        results.forEach((station) => {
          if (station) {
            updated[station._id] = station;
          }
        });
        return updated;
      });

      setIsFetching(false);
    };

    fetchAllStations();
  }, [dispatch, selectedDevices]);

  /* ------------------------------------------------------------------------ */
  /*                              Handlers                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Handle toggle click
   * @param key Toggle key
   */
  const handleClickToggle = useCallback((key: string) => {
    setChartToggle((previous) =>
      previous.map((tab) => ({
        ...tab,
        isActive: tab.key === key,
      })),
    );
  }, []);

  /**
   * Handle dropdown value change
   * @param index Column index
   * @param value Selected station ID
   */
  const handleChangeFunction = useCallback((index: number, value: string) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      updated[index] = value;
      return updated;
    });
  }, []);

  /**
   * Swap two comparison columns
   * @param first First index
   * @param second Second index
   */
  const swapColumns = useCallback((first: number, second: number) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      [updated[first], updated[second]] = [updated[second], updated[first]];
      return updated;
    });
  }, []);

  /**
   * Filter dropdown options to avoid duplicate selection
   * @param currentIndex Current dropdown index
   * @returns Filtered options
   */
  const getFilteredOptions = useCallback(
    (currentIndex: number): SelectOption[] =>
      orderedDeviceOptions.filter(
        (option) =>
          option.value === selectedDevices[currentIndex] || !selectedDevices.includes(option.value),
      ),
    [orderedDeviceOptions, selectedDevices],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Render                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="station-comparison-list" dir={i18n.dir()}>
      <div className="time-period-toggle">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
        />
        <ExportButton
          label={t('exportData')}
          buttonList={[
            {
              key: 'csv',
              /**
               * Export csv function
               */
              onClickFunction: () => handleExport('csv'),
              allow: true,
            },
            {
              key: 'pdf',
              /**
               * Export pdf function
               */
              onClickFunction: () => handleExport('pdf'),
              allow: true,
            },
          ]}
          loader={isFetching}
          placement="top"
        />
      </div>
      <div className="selectors-list">
        <div className="station-comparison-toggle">
          <ToggleWithIcons
            leftMenu={chartToggle[0]}
            rightMenu={chartToggle[1]}
            handleClickToggle={handleClickToggle}
          />
        </div>

        {selectedDevices.slice(0, visibleSelectorCount).map((deviceId, index) => (
          <div key={`station-selector-${index}`} className="selector-item">
            <div className="select-wrapper position">
              <SelectField
                options={getFilteredOptions(index)}
                placeholder={t('selectStation')}
                selectedValue={deviceId}
                name={`selectedDevice${index}`}
                onChangeValue={(_, value) => handleChangeFunction(index, value)}
                enableSearch
              />
              {index > 0 && deviceId && (
                <button
                  type="button"
                  className="clear-selected-station"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeFunction(index, '');
                  }}
                  aria-label={t('remove')}
                  title={t('remove')}>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>

            {index < visibleSelectorCount - 1 && (
              <div className="swap-button">
                <SwapIcon className="swap-icon" onClick={() => swapColumns(index, index + 1)} />
              </div>
            )}
          </div>
        ))}
      </div>

      <StationCard
        stations={detailedStations}
        metrics={METRICS}
        heading={t('stationComparison')}
        differenceOnly={chartToggle[1].isActive}
        isLoading={isFetching}
      />
    </div>
  );
}

export default StationComparisonList;
